import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { 
  CheckCircle, XCircle, AlertTriangle, Calendar, MapPin, 
  User, FileText, Download, Trash2, Lightbulb, RefreshCw, X
} from 'lucide-react';
import { AnalysisProvenanceBadge } from './AnalysisProvenanceBadge';
import { waitForAuditAnalysis } from '@/utils/auditAnalysis';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalizedAI, getLocalizedHazards } from '@/utils/localizedAI';
import PolicyViolations from '@/components/PolicyViolations';
import AuthedImage from '@/components/AuthedImage';
import { PotentialHazards } from '@/components/PotentialHazards';

const ViewAuditDialog = ({ audit, open, onClose, onDelete, onUpdate, user }) => {
  const { t } = useTranslation();
  const [jsaRecommendations, setJsaRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [retryingAnalysis, setRetryingAnalysis] = useState(null); // Track which photo is being retried
  const [localAudit, setLocalAudit] = useState(null); // Local state for immediate UI updates
  const [lightboxPhoto, setLightboxPhoto] = useState(null); // { path, index }
  
  // Update local audit when prop changes
  useEffect(() => {
    if (audit) {
      setLocalAudit(audit);
    }
  }, [audit]);

  // Load JSA recommendations when audit dialog opens
  useEffect(() => {
    const loadJSARecommendations = async () => {
      if (!audit?.id) return;
      
      try {
        setLoadingRecommendations(true);
        console.log(`🔍 Loading JSA recommendations for audit ${audit.id}...`);
        const response = await axiosInstance.get(`/audits/${audit.id}/jsa-recommendations`);
        console.log('📥 JSA Recommendations response:', response.data);
        setJsaRecommendations(response.data);
      } catch (error) {
        console.error('❌ Failed to load JSA recommendations:', error);
        console.error('Error details:', error.response?.data);
        // Don't show error toast, recommendations might not exist yet
        setJsaRecommendations(null);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    if (audit && open && (audit.status === 'completed' || audit.status === 'analyzed')) {
      loadJSARecommendations();
    } else {
      // Reset recommendations when dialog is closed or audit is not completed
      setJsaRecommendations(null);
      setLoadingRecommendations(false);
    }
  }, [audit, open]);
  
  // Retry photo analysis handler
  const handleRetryAnalysis = async (photoUrl) => {
    if (!localAudit) return;
    setRetryingAnalysis(photoUrl);
    try {
      const response = await axiosInstance.post(
        `/safety-audits/${localAudit.id}/retry-photo-analysis`,
        null,
        { params: { photo_url: photoUrl } }
      );
      
      if (response.data.analysis) {
        // Update local state immediately
        setLocalAudit(prev => ({
          ...prev,
          photoAnalyses: [...(prev.photoAnalyses || []), response.data.analysis]
        }));
        
        toast.success('Photo analysis completed!');
        
        // Notify parent to refresh if callback provided
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Failed to retry analysis:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to analyze photo. Please try again.';
      toast.error(errorMessage);
    } finally {
      setRetryingAnalysis(null);
    }
  };

  // Retry full AI audit analysis (when initial analysis failed / Pending)
  const [retryingAudit, setRetryingAudit] = useState(false);
  const handleRetryAuditAnalysis = async () => {
    if (!localAudit) return;
    setRetryingAudit(true);
    try {
      toast.info('Re-running AI analysis... this may take up to 60 seconds.');
      const aiResp = await axiosInstance.post(
        `/safety-audits/${localAudit.id}/analyze`,
        null,
        { timeout: 90000 }
      );
      // Patch the audit with returned AI fields (analyze endpoint also persists)
      setLocalAudit(prev => ({
        ...prev,
        aiRiskScore: aiResp.data?.aiRiskScore || prev.aiRiskScore,
        aiSafetyScore: aiResp.data?.aiSafetyScore ?? prev.aiSafetyScore,
        aiHazardSummary: aiResp.data?.aiHazardSummary || prev.aiHazardSummary,
        aiCorrectiveActions: aiResp.data?.aiCorrectiveActions || prev.aiCorrectiveActions,
        aiTrainingRecommendations: aiResp.data?.aiTrainingRecommendations || prev.aiTrainingRecommendations,
        aiMissingInfo: aiResp.data?.aiMissingInfo || prev.aiMissingInfo,
        aiJsaUpdates: aiResp.data?.aiJsaUpdates || prev.aiJsaUpdates,
        jsaComplianceCheck: aiResp.data?.jsaComplianceCheck || prev.jsaComplianceCheck,
        aiAnalysisProvenanceBadge:
          aiResp.data?.aiAnalysisProvenanceBadge || prev.aiAnalysisProvenanceBadge,
        status: 'analyzed',
      }));
      toast.success('AI analysis completed!');
      if (onUpdate) onUpdate();
      // Refresh JSA recommendations
      try {
        const recResponse = await axiosInstance.get(`/audits/${localAudit.id}/jsa-recommendations`);
        setJsaRecommendations(recResponse.data);
      } catch (e) {
        console.log('No JSA recommendations yet:', e?.message);
      }
    } catch (err) {
      console.error('Retry AI analysis failed:', err);
      // A slow analysis still finishes on the server, so wait for it before reporting a failure.
      toast.info('Still analysing — waiting for the result...');
      const settled = await waitForAuditAnalysis(localAudit.id);
      if (settled) {
        setLocalAudit(prev => ({ ...prev, ...settled, status: 'analyzed' }));
        toast.success('AI analysis completed!');
        if (onUpdate) onUpdate();
        setRetryingAudit(false);
        return;
      }
      const msg = err.code === 'ECONNABORTED'
        ? 'AI analysis timed out. Please try again.'
        : (err.response?.data?.detail || 'Failed to run AI analysis. Please try again.');
      toast.error(msg);
    } finally {
      setRetryingAudit(false);
    }
  };

  // Early return if no audit data - AFTER all hooks
  if (!localAudit) return null;

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete this audit?\n\nLocation: ${localAudit.location}\nDate: ${new Date(localAudit.auditDate).toLocaleDateString()}\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/safety-audits/${localAudit.id}`);
      toast.success('Audit deleted successfully');
      if (onDelete) onDelete();
      onClose();
    } catch (error) {
      console.error('Failed to delete audit:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete audit');
    }
  };

  const handleExportPDF = async (format = 'comprehensive') => {
    try {
      toast.info('Generating PDF...');
      const response = await axiosInstance.get(`/safety-audits/${localAudit.id}/export-pdf`, {
        params: { format },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `audit-${format}-${localAudit.location.replace(/\s+/g, '-')}-${new Date(localAudit.auditDate).toISOString().split('T')[0]}.pdf`;
      
      // Check if running in Capacitor (mobile app)
      if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
        // Mobile: Use Capacitor Filesystem
        try {
          const { Filesystem, Directory } = await import('@capacitor/filesystem');
          const { Share } = await import('@capacitor/share');
          
          // Convert blob to base64
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64Data = reader.result.split(',')[1];
            
            // Write file to device
            const result = await Filesystem.writeFile({
              path: fileName,
              data: base64Data,
              directory: Directory.Documents,
            });
            
            // Share/Open the file
            await Share.share({
              title: 'Safety Audit Report',
              text: 'Safety Audit PDF Report',
              url: result.uri,
              dialogTitle: 'Share or Open PDF'
            });
            
            toast.success('PDF ready! Choose where to save or share.');
          };
          reader.readAsDataURL(blob);
        } catch (mobileError) {
          console.error('Mobile download error:', mobileError);
          // Fallback to opening in new tab
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          toast.info('PDF opened in new tab. Long-press to save.');
        }
      } else {
        // Desktop: Standard download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded successfully');
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'pass') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (status === 'fail') return <XCircle className="w-4 h-4 text-red-600" />;
    return <span className="text-gray-400 text-xs">N/A</span>;
  };

  // Use the backend's authoritative figures so the dialog, the list badge and the PDF agree.
  // Falls back to a local calculation only for offline/unsaved audits.
  const getComplianceScore = () => {
    if (localAudit.calculatedScoring) {
      return localAudit.calculatedScoring.applicable_items > 0
        ? localAudit.calculatedScoring.compliance_rate
        : 'N/A';
    }
    if (!localAudit.checklist) return 'N/A';
    
    let totalChecks = 0;
    let passedChecks = 0;
    
    Object.values(localAudit.checklist).forEach(category => {
      Object.values(category).forEach(check => {
        if (check.status !== 'na') {
          totalChecks++;
          if (check.status === 'pass') passedChecks++;
        }
      });
    });
    
    if (totalChecks === 0) return 'N/A';
    return Math.round((passedChecks / totalChecks) * 100);
  };

  // Don't render if no audit data
  if (!audit) return null;

  const complianceScore = getComplianceScore();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl">Safety Audit Details</DialogTitle>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="export-pdf-menu-trigger">
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => handleExportPDF('standard')}
                    data-testid="export-pdf-standard"
                  >
                    {t('audits.standardReport')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleExportPDF('comprehensive')}
                    data-testid="export-pdf-comprehensive"
                  >
                    {t('audits.comprehensiveReport')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button 
                onClick={handleDelete} 
                size="sm" 
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:border-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-semibold">{new Date(localAudit.auditDate).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-semibold">{localAudit.location}</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="view-audit-project">
              <FileText className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">{t('audits.project')}</p>
                <p className="font-semibold">{localAudit.projectName || t('audits.unassigned')}</p>
                {localAudit.projectSite && (
                  <p className="text-xs text-gray-500">{localAudit.projectSite}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Supervisor</p>
                <p className="font-semibold">{localAudit.supervisorName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">{t('audits.complianceRate')}</p>
                <p className="font-semibold">
                  {complianceScore !== 'N/A' ? (
                    <span className={`${
                      ['HIGH', 'CRITICAL'].includes(localAudit.calculatedScoring?.risk_level) ? 'text-red-600' :
                      complianceScore >= 80 ? 'text-green-600' :
                      complianceScore >= 60 ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {complianceScore}%
                    </span>
                  ) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Crew & Project */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Crew Involved</p>
                  <p className="font-medium">{localAudit.crewInvolved || 'N/A'}</p>
                </div>
                {localAudit.workDescription && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Work Description</p>
                    <p className="font-medium text-sm">{localAudit.workDescription}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Checklist */}
          {localAudit.checklist && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-4">Safety Checklist</h3>
                <div className="space-y-4">
                  {Object.entries(localAudit.checklist).map(([categoryKey, category]) => (
                    <div key={categoryKey}>
                      <h4 className="font-medium text-gray-700 mb-2 capitalize">
                        {categoryKey.replace(/([A-Z])/g, ' $1').trim()}
                      </h4>
                      <div className="space-y-2 pl-4">
                        {Object.entries(category).map(([itemKey, item]) => (
                          <div key={itemKey} className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm capitalize">
                              {itemKey.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(item.status)}
                              {item.notes && (
                                <span className="text-xs text-gray-500 ml-2">{item.notes}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Findings */}
          {localAudit.detailedFindings && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-2">Detailed Findings</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{localAudit.detailedFindings}</p>
              </CardContent>
            </Card>
          )}

          {/* Unusual Hazards */}
          {localAudit.unusualHazards && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  Unusual Hazards
                </h3>
                <p className="text-gray-700 whitespace-pre-wrap">{localAudit.unusualHazards}</p>
              </CardContent>
            </Card>
          )}

          {/* Failure Explanations */}
          {localAudit.failureExplanations && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-2 text-red-600">Failure Explanations</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{localAudit.failureExplanations}</p>
              </CardContent>
            </Card>
          )}

          {/* Photo Evidence with AI Analysis */}
          {localAudit.photoUrls && localAudit.photoUrls.length > 0 && (
            <Card className="border-blue-200" data-testid="audit-photo-evidence-card">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  📸 Photo Evidence ({localAudit.photoUrls.length} photos)
                </h3>

                {/* Preview strip — quick visual scan before downloading PDF */}
                <div className="flex gap-2 overflow-x-auto pb-3 mb-4 border-b border-blue-100">
                  {localAudit.photoUrls.map((photoUrl, idx) => (
                    <button
                      key={`strip-${idx}`}
                      type="button"
                      onClick={() => setLightboxPhoto({ path: photoUrl, index: idx })}
                      data-testid={`audit-photo-strip-${idx}`}
                      className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 focus:border-blue-600 focus:outline-none transition-all"
                      aria-label={`Enlarge photo ${idx + 1}`}
                    >
                      <AuthedImage
                        storedPath={photoUrl}
                        alt={`Audit photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[10px] px-1 rounded-tl">{idx + 1}</span>
                    </button>
                  ))}
                </div>
                
                <div className="space-y-6">
                  {localAudit.photoUrls.map((photoUrl, idx) => {
                    // Find analysis for this photo
                    const analysis = localAudit.photoAnalyses?.find(a => a.photoUrl === photoUrl);
                    const isRetrying = retryingAnalysis === photoUrl;
                    
                    return (
                      <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start gap-4">
                          {/* Photo thumbnail */}
                          <button
                            type="button"
                            onClick={() => setLightboxPhoto({ path: photoUrl, index: idx })}
                            data-testid={`audit-photo-thumb-${idx}`}
                            className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden hover:ring-2 hover:ring-blue-500 focus:ring-2 focus:ring-blue-600 focus:outline-none transition"
                            aria-label={`Enlarge photo ${idx + 1}`}
                          >
                            <AuthedImage
                              storedPath={photoUrl}
                              alt={`Audit photo ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                          
                          {/* Analysis details */}
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 mb-2">Photo {idx + 1}</p>
                            
                            {analysis ? (
                              <>
                                {/* Scores */}
                                <div className="flex gap-3 mb-3">
                                  <Badge className={`${
                                    analysis.overallSafetyScore >= 80 ? 'bg-green-600' :
                                    analysis.overallSafetyScore >= 60 ? 'bg-orange-500' :
                                    'bg-red-600'
                                  }`}>
                                    Safety: {analysis.overallSafetyScore}/100
                                  </Badge>
                                  <Badge className={`${
                                    analysis.overallErgonomicsScore >= 80 ? 'bg-green-600' :
                                    analysis.overallErgonomicsScore >= 60 ? 'bg-orange-500' :
                                    'bg-red-600'
                                  }`}>
                                    Ergonomics: {analysis.overallErgonomicsScore}/100
                                  </Badge>
                                  <Badge className={`${
                                    analysis.riskLevel === 'Low' ? 'bg-green-600' :
                                    analysis.riskLevel === 'Medium' ? 'bg-orange-500' :
                                    'bg-red-600'
                                  }`}>
                                    Risk: {analysis.riskLevel}
                                  </Badge>
                                </div>
                                
                                {/* Safety Findings */}
                                {analysis.safetyFindings && analysis.safetyFindings.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-sm font-medium text-gray-700">Safety Findings:</p>
                                    <ul className="text-xs text-gray-600 space-y-1 mt-1">
                                      {analysis.safetyFindings.slice(0, 3).map((finding, fIdx) => (
                                        <li key={fIdx} className="flex items-start gap-1">
                                          {finding.compliant ? 
                                            <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" /> : 
                                            <XCircle className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" />
                                          }
                                          <span>
                                            <strong className={finding.compliant ? 'text-green-700' : 'text-red-700'}>
                                              {finding.category}
                                            </strong>: {finding.issue}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Ergonomics Findings */}
                                {analysis.ergonomicsFindings && analysis.ergonomicsFindings.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-sm font-medium text-gray-700">Ergonomics Findings:</p>
                                    <ul className="text-xs text-gray-600 space-y-1 mt-1">
                                      {analysis.ergonomicsFindings.slice(0, 2).map((finding, fIdx) => (
                                        <li key={fIdx} className="flex items-start gap-1">
                                          <AlertTriangle className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                                            finding.riskLevel === 'High' ? 'text-red-600' :
                                            finding.riskLevel === 'Medium' ? 'text-orange-500' :
                                            'text-yellow-500'
                                          }`} />
                                          <span>
                                            <strong>{finding.category}</strong> ({finding.bodyPartAffected}): {finding.issue}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Immediate Actions */}
                                {analysis.immediateActions && analysis.immediateActions.length > 0 && (
                                  <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
                                    <p className="text-xs font-bold text-red-700">⚠️ IMMEDIATE ACTIONS:</p>
                                    <ul className="text-xs text-red-600 mt-1">
                                      {analysis.immediateActions.map((action, aIdx) => (
                                        <li key={aIdx}>• {action}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="flex flex-col gap-2">
                                <p className="text-sm text-gray-500 italic">AI analysis not available for this photo</p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRetryAnalysis(photoUrl)}
                                  disabled={isRetrying}
                                  className="w-fit text-blue-600 border-blue-300 hover:bg-blue-50"
                                >
                                  <RefreshCw className={`w-3 h-3 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
                                  {isRetrying ? 'Analyzing...' : 'Retry Analysis'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Analysis */}
          {(() => {
            const riskScore = localAudit.aiRiskScore || '';
            // v2 analyses no longer produce an AI risk score, so "was it analysed?" is answered
            // by the analysis itself, not by the retired score field.
            const analysed = localAudit.aiAnalysisVersion === 'v2'
              || localAudit.status === 'analyzed'
              || (riskScore && !['pending', 'unknown'].includes(riskScore.toLowerCase()));
            const isPendingOrEmpty = !analysed;
            return (
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">AI Analysis</h3>
                    {isPendingOrEmpty && (
                      <Button
                        size="sm"
                        onClick={handleRetryAuditAnalysis}
                        disabled={retryingAudit}
                        data-testid="retry-ai-analysis-button"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${retryingAudit ? 'animate-spin' : ''}`} />
                        {retryingAudit ? 'Analyzing...' : 'Retry AI Analysis'}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{t('audits.officialRisk')}</p>
                      <Badge
                        className={`${
                          !localAudit.calculatedScoring ? 'bg-gray-400' :
                          ['HIGH', 'CRITICAL'].includes(localAudit.calculatedScoring.risk_level) ? 'bg-red-600' :
                          localAudit.calculatedScoring.risk_level === 'MODERATE' ? 'bg-orange-500' :
                          'bg-green-600'
                        }`}
                        data-testid="audit-calculated-risk-badge"
                      >
                        {localAudit.calculatedScoring?.risk_level || 'Not calculated'}
                      </Badge>
                      {localAudit.calculatedScoring?.risk_reason && (
                        <p className="text-xs text-gray-600 mt-1">
                          Because {localAudit.calculatedScoring.risk_reason}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{t('audits.complianceRate')}</p>
                      <p className="font-semibold" data-testid="audit-calculated-compliance">
                        {complianceScore === 'N/A' ? 'N/A' : `${complianceScore}%`}
                      </p>
                      {localAudit.calculatedScoring && (
                        <p className="text-xs text-gray-600 mt-1">
                          {localAudit.calculatedScoring.passed_checks} passed /{' '}
                          {localAudit.calculatedScoring.applicable_items} applicable
                        </p>
                      )}
                    </div>
                  </div>

                  {(riskScore || localAudit.aiSafetyScore) && (
                    <p className="text-xs text-gray-500 mt-3 italic" data-testid="audit-legacy-ai-note">
                      Legacy AI values recorded with this audit (not used for the official
                      classification): risk {riskScore || 'n/a'}
                      {localAudit.aiSafetyScore ? `, safety score ${localAudit.aiSafetyScore}/100` : ''}.
                    </p>
                  )}

                  {isPendingOrEmpty && (
                    <p className="text-xs text-gray-600 mt-3 italic">
                      AI analysis was not completed. Click "Retry AI Analysis" above to generate hazard analysis, corrective actions, and JSA recommendations.
                    </p>
                  )}

                  {localAudit.aiAnalysisProvenanceBadge?.hasAnalysis && (
                    <div className="mt-4">
                      <AnalysisProvenanceBadge badge={localAudit.aiAnalysisProvenanceBadge} />
                    </div>
                  )}

                  {localAudit.aiAnalysisWithheldNotice && (
                    <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3"
                         data-testid="audit-analysis-withheld">
                      <p className="text-sm font-medium text-red-800">
                        {localAudit.aiAnalysisWithheldNotice}
                      </p>
                    </div>
                  )}

                  {getLocalizedAI(localAudit, 'aiHazardSummary') && (
                    <div className="mt-4" data-testid="audit-hazard-summary">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        {t('audits.hazardSummary')}
                      </p>
                      <p className="text-sm text-gray-700">
                        {getLocalizedAI(localAudit, 'aiHazardSummary')}
                      </p>
                    </div>
                  )}

                  {(getLocalizedAI(localAudit, 'aiCorrectiveActions') || []).length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">{t('audits.recommendations')}</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                        {getLocalizedAI(localAudit, 'aiCorrectiveActions').map((action, idx) => (
                          <li key={idx}>{action}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(getLocalizedAI(localAudit, 'aiPreventiveMeasures') || []).length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">{t('potentialHazards.preventiveMeasuresTitle')}</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700" data-testid="audit-preventive-measures">
                        {getLocalizedAI(localAudit, 'aiPreventiveMeasures').map((measure, idx) => (
                          <li key={idx}>{measure}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(getLocalizedAI(localAudit, 'aiTrainingRecommendations') || []).length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">{t('training.title')}</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                        {getLocalizedAI(localAudit, 'aiTrainingRecommendations').map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* AI-identified potential hazards — unverified, separate from findings */}
          <PotentialHazards
            auditId={localAudit.id}
            hazards={getLocalizedHazards(localAudit)}
            canReview={['supervisor', 'company_admin', 'division_admin', 'super_admin', 'superintendent'].includes(user?.role)}
            onUpdated={(updated) => setLocalAudit((prev) => ({
              ...prev,
              aiPotentialHazards: (prev.aiPotentialHazards || []).map((h) => (h.id === updated.id ? updated : h)),
            }))}
          />

          {/* Policy Violations Panel */}
          {(localAudit.status === 'completed' || localAudit.status === 'analyzed') && (
            <PolicyViolations
              recordKind="audit"
              recordId={localAudit.id}
              initialViolations={localAudit.policyViolations || []}
              summary={localAudit.policyViolationsSummary || ''}
              scannedAt={localAudit.policyViolationsScannedAt || ''}
            />
          )}

          {/* JSA Compliance Check */}
          {localAudit.jsaComplianceCheck && localAudit.jsaComplianceCheck.jsas_found && (
            <Card className="border-purple-200">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600" />
                  JSA Compliance Check
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {localAudit.jsaComplianceCheck.matched_jsas?.length || 0} JSA(s) matched for this work
                </p>
                {localAudit.jsaComplianceCheck.matched_jsas?.map((jsa, idx) => (
                  <div key={idx} className="border rounded-lg p-3 mb-2 bg-gray-50">
                    <p className="font-medium">{jsa.jsa_title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`${
                        jsa.compliance_analysis?.compliance_status === 'COMPLIANT' ? 'bg-green-600' :
                        jsa.compliance_analysis?.compliance_status === 'PARTIAL' ? 'bg-orange-500' :
                        'bg-red-600'
                      }`}>
                        {jsa.compliance_analysis?.compliance_status}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        Match: {jsa.match_confidence}%
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* AI JSA Recommendations */}
          {(localAudit.status === 'completed' || localAudit.status === 'analyzed') && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-600" />
                  AI JSA Recommendations
                </h3>
                {loadingRecommendations ? (
                  <p className="text-sm text-gray-500">Loading recommendations...</p>
                ) : (() => {
                  // Debug: Log the data structure
                  console.log('🔍 Rendering recommendations. Data:', {
                    hasRecommendations: !!jsaRecommendations,
                    recommendationsArray: jsaRecommendations?.recommendations,
                    arrayLength: jsaRecommendations?.recommendations?.length,
                    firstDoc: jsaRecommendations?.recommendations?.[0],
                    firstDocRecs: jsaRecommendations?.recommendations?.[0]?.recommendations
                  });

                  // Check if we have any recommendations
                  const hasRecommendations = jsaRecommendations?.recommendations?.length > 0 && 
                                            jsaRecommendations.recommendations[0]?.recommendations?.length > 0;
                  
                  if (!hasRecommendations) {
                    return (
                      <p className="text-sm text-gray-500">
                        No JSA recommendations available yet. To generate recommendations: Mark an audit as "completed" at a location with existing JSAs, and the system will automatically analyze the audit findings to suggest JSA improvements.
                      </p>
                    );
                  }

                  const recDoc = jsaRecommendations.recommendations[0];
                  const recommendations = recDoc.recommendations;

                  return (
                    <div>
                      <p className="text-sm text-gray-600 mb-3">
                        Based on this audit's findings, here are {recommendations.length} AI-generated recommendation{recommendations.length !== 1 ? 's' : ''} to improve JSAs at this location:
                      </p>
                      <div className="space-y-3">
                        {recommendations.map((rec, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-white">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={`${
                                    rec.priority === 'High' ? 'bg-red-600' :
                                    rec.priority === 'Medium' ? 'bg-orange-500' :
                                    'bg-blue-600'
                                  }`}>
                                    {rec.priority}
                                  </Badge>
                                  <span className="text-xs font-medium text-gray-600 uppercase">
                                    {rec.recommendationType?.replace('_', ' ')}
                                  </span>
                                </div>
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  {rec.recommendation}
                                </p>
                                {rec.reason && (
                                  <p className="text-xs text-gray-500 italic">
                                    Reason: {rec.reason}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-3 italic">
                        Status: {recDoc.status} | Generated: {new Date(recDoc.generatedAt).toLocaleString()}
                      </p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
          data-testid="audit-photo-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxPhoto(null); }}
            data-testid="audit-photo-lightbox-close"
            className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black/50 rounded-full p-2"
            aria-label="Close photo viewer"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[90vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <AuthedImage
              storedPath={lightboxPhoto.path}
              alt={`Audit photo ${lightboxPhoto.index + 1}`}
              className="max-h-[80vh] max-w-full object-contain rounded"
            />
            <p className="text-white text-sm mt-4">
              Photo {lightboxPhoto.index + 1} of {localAudit.photoUrls.length}
            </p>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ViewAuditDialog;
